/**
 * S-06 «Активные входы» (ПС §15, BS §5.5, §17.2).
 *
 * Изменение v1.12. Список перестал быть перечнем имён: каждая строка открывается карточкой
 * сеанса — клиент и его версия, IP-адрес, страна, регион и город, когда вход выполнен и когда
 * устройство было в сети, — и оттуда же сеанс завершается. Раскладка повторяет «Устройства»
 * Telegram: сначала это устройство, затем красная строка «завершить все остальные» со сноской,
 * затем остальные сеансы.
 *
 * Референсы Mobbin: Telegram Devices (https://mobbin.com/screens/d5905262-96e9-4793-b9c9-7d55b06c1379)
 * — группировка и красное действие с подтверждением; MoonPay Devices
 * (https://mobbin.com/screens/75143ffd-274b-48d6-82aa-f04b69c1d784) — строки с шевроном и сноска
 * «Logs out all devices except for this one»; Temu Sign in activity
 * (https://mobbin.com/screens/0c9545ad-ff0c-467c-932b-6b347095a566) — IP и место прямо в строке;
 * UNIQLO Previous login (https://mobbin.com/screens/42232fac-b506-4e9f-8da7-b10e4343e3cb) —
 * карточка входа как список «поле — значение».
 */
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { api, unwrap } from '@shared/api/client'
import type { Session } from '@shared/api/types'
import { Button, Dialog, Icon, Sheet, Spinner, useToast } from '@shared/ui'

import { formatDateTime, formatListTime } from '../../lib/format'
import styles from './settings.module.css'

export function SessionsSection(): React.ReactElement {
  const toast = useToast()
  const [opened, setOpened] = useState<Session | null>(null)
  const [confirmAll, setConfirmAll] = useState(false)
  const [busy, setBusy] = useState(false)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['sessions'],
    queryFn: async () => unwrap(await api.GET('/auth/sessions')).items,
  })

  if (isLoading) return <Spinner size={24} />

  const sessions = data ?? []
  const current = sessions.find((s) => s.current) ?? null
  const others = sessions.filter((s) => !s.current)

  const revoke = async (session: Session): Promise<void> => {
    setBusy(true)
    try {
      await api.DELETE('/auth/sessions/{sessionId}', { params: { path: { sessionId: session.id } } })
      setOpened(null)
      toast.show(`${deviceTitle(session)} — сеанс завершён`)
      await refetch()
    } catch {
      toast.show('Не удалось завершить сеанс', { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  const revokeOthers = async (): Promise<void> => {
    setBusy(true)
    try {
      const { revoked } = unwrap(await api.POST('/auth/sessions/revoke-others'))
      setConfirmAll(false)
      toast.show(
        revoked === 0 ? 'Других сеансов не было' : revoked === 1 ? 'Сеанс завершён' : 'Сеансы завершены',
      )
      await refetch()
    } catch {
      toast.show('Не удалось завершить сеансы', { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      {current ? (
        <>
          <h2 className={styles['groupTitle']}>Это устройство</h2>
          <div className={styles['card']}>
            <SessionRow session={current} onOpen={setOpened} />
          </div>
        </>
      ) : null}

      {others.length > 0 ? (
        <>
          <div className={styles['card']} style={{ marginTop: 'var(--space-4)' }}>
            <button type="button" className={styles['revokeAllRow']} onClick={() => setConfirmAll(true)}>
              <span className={styles['revokeAllIcon']} aria-hidden>
                <Icon name="logout" size={18} />
              </span>
              Завершить все другие сеансы
            </button>
          </div>
          <p className={styles['footnote']}>
            Выход со всех устройств, кроме этого. Уведомления на них перестанут приходить, а для возврата
            понадобится новый код.
          </p>

          <h2 className={styles['groupTitle']}>Активные сеансы</h2>
          <div className={styles['card']}>
            {others.map((session) => (
              <SessionRow key={session.id} session={session} onOpen={setOpened} />
            ))}
          </div>
          <p className={styles['footnote']}>
            Нажмите на сеанс, чтобы посмотреть, откуда выполнен вход. Если какой-то из них не ваш — завершите
            его и смените способ входа.
          </p>
        </>
      ) : (
        <p className={styles['footnote']}>
          Других активных входов нет. Здесь появятся все устройства, на которых выполнен вход в аккаунт, — с
          адресом и местом входа.
        </p>
      )}

      <SessionSheet
        session={opened}
        busy={busy}
        onClose={() => setOpened(null)}
        onRevoke={() => void (opened && revoke(opened))}
      />

      <Dialog
        open={confirmAll}
        onClose={() => setConfirmAll(false)}
        title="Завершить все другие сеансы?"
        description={`${
          others.length === 1
            ? 'Ещё одно устройство выйдет'
            : `Ещё ${others.length} ${devices(others.length)} выйдут`
        } из аккаунта. Это устройство останется — уведомления будут приходить только на него.`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmAll(false)} disabled={busy}>
              Отмена
            </Button>
            <Button variant="danger" onClick={() => void revokeOthers()} loading={busy}>
              Завершить
            </Button>
          </>
        }
      />
    </>
  )
}

/* ===================================================================== */
/* Строка списка                                                          */
/* ===================================================================== */

function SessionRow({
  session,
  onOpen,
}: {
  session: Session
  onOpen: (session: Session) => void
}): React.ReactElement {
  return (
    <button type="button" className={styles['deviceRow']} onClick={() => onOpen(session)}>
      <span className={styles['deviceIcon']} aria-hidden>
        <Icon name={session.platform === 'android' ? 'phone' : 'monitor'} size={20} />
      </span>

      <span className={styles['deviceBody']}>
        <span className={styles['deviceName']}>{deviceTitle(session)}</span>
        <span className={styles['deviceMeta']}>{appLine(session)}</span>
        <span className={styles['deviceMeta']}>{whereAndWhen(session)}</span>
      </span>

      <Icon name="chevron-right" size={18} className={styles['deviceChevron']} />
    </button>
  )
}

/* ===================================================================== */
/* Карточка сеанса                                                        */
/* ===================================================================== */

function SessionSheet({
  session,
  busy,
  onClose,
  onRevoke,
}: {
  session: Session | null
  busy: boolean
  onClose: () => void
  onRevoke: () => void
}): React.ReactElement | null {
  if (!session) return null

  const place = placeLine(session)

  return (
    <Sheet open onClose={onClose} title={deviceTitle(session)} description={appLine(session)}>
      <dl className={styles['deviceFacts']}>
        <Fact label="Приложение" value={appName(session)} />
        <Fact label="Версия" value={session.app_version} />
        {/* У приложения клиент и есть приложение — строку-двойник не показываем. */}
        <Fact label="Клиент" value={session.client === appName(session) ? null : session.client} />
        <Fact label="IP-адрес" value={session.ip} mono />
        <Fact label="Расположение" value={place} />
        <Fact label="Вход выполнен" value={formatDateTime(session.created_at)} />
        <Fact
          label="Последняя активность"
          value={session.current ? 'сейчас в сети' : formatDateTime(session.last_active_at)}
        />
      </dl>

      {session.current ? (
        <p className={styles['deviceNote']}>
          Это устройство. Его сеанс завершается выходом из аккаунта — действие «Выйти» в настройках.
        </p>
      ) : (
        <>
          <Button variant="danger" fullWidth size="lg" loading={busy} onClick={onRevoke}>
            Завершить сеанс
          </Button>
          <p className={styles['deviceNote']}>
            Устройство выйдет из аккаунта сразу и перестанет получать уведомления.
          </p>
        </>
      )}
    </Sheet>
  )
}

function Fact({
  label,
  value,
  mono = false,
}: {
  label: string
  value: string | null | undefined
  mono?: boolean
}): React.ReactElement | null {
  if (!value) return null
  return (
    <div className={styles['deviceFact']}>
      <dt className={styles['deviceFactLabel']}>{label}</dt>
      <dd className={mono ? styles['deviceFactMono'] : styles['deviceFactValue']}>{value}</dd>
    </div>
  )
}

/* ===================================================================== */
/* Подписи                                                                */
/* ===================================================================== */

/** Имя устройства: его прислал клиент, иначе — то, что известно о самом клиенте. */
export function deviceTitle(session: Session): string {
  return session.device_name ?? session.client ?? appName(session)
}

/** Какое приложение держит сеанс. Заголовок уже назвал устройство, здесь — само приложение. */
function appName(session: Session): string {
  return session.platform === 'android' ? 'Движ для Android' : 'Движ в браузере'
}

/** Вторая строка: приложение и его версия — «Движ для Android · 1.4.0». */
function appLine(session: Session): string {
  const app = appName(session)
  return session.app_version ? `${app} · ${session.app_version}` : app
}

/** Место без повторов: «Москва, Россия», но не «Москва, Москва, Россия». */
export function placeLine(session: Session): string {
  const parts = [session.city, session.region, session.country].filter(
    (part, index, all): part is string => Boolean(part) && all.indexOf(part) === index,
  )
  return parts.join(', ')
}

/** Третья строка: где и когда. Без геопозиции остаётся адрес, без адреса — только время. */
export function whereAndWhen(session: Session): string {
  // В строке списка время короткое («вчера», «17:00»): полная дата не помещается рядом с местом.
  const when = session.current ? 'сейчас в сети' : formatListTime(session.last_active_at)
  const where = placeLine(session) || session.ip
  return where ? `${where} · ${when}` : when
}

/** «2 устройства», «5 устройств» — склонение по числу. */
function devices(count: number): string {
  const tail = count % 100
  if (tail >= 11 && tail <= 14) return 'устройств'
  switch (count % 10) {
    case 1:
      return 'устройство'
    case 2:
    case 3:
    case 4:
      return 'устройства'
    default:
      return 'устройств'
  }
}
